import type { DetailedHTMLProps, InputHTMLAttributes } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useUpdateEffect } from '@/renderer/utils/hooks/useUpdateEffect';
import { IS_SERVER } from '@/renderer/utils/ssr';
import { useIdleContext } from '@/renderer/utils/useIdle';
import useMe, { useAuthToken } from '@/renderer/utils/useMe';
import { usePageContext } from '@/renderer/utils/usePageContext';
import useWebSocket, { WS_BASE_URL } from '@/renderer/utils/useWebSocket2';

export interface WebSocketUnreadCounterProps<T = unknown> {
  dataName: string;
  dataPath: string;
  dataCb?: (
    event: MessageEvent,
    data: API.SubscriptionResponse<T>,
    setCount: (value: React.SetStateAction<number>) => void,
    mapRef: React.MutableRefObject<Map<string | number, number>>,
    me: API.User | null | undefined,
  ) => void;
  dataNavCb?: (
    urlPathname: string,
    setCount: (value: React.SetStateAction<number>) => void,
    mapRef: React.MutableRefObject<Map<string | number, number>>,
    me: API.User | null | undefined,
  ) => void;
}

export default function WebSocketUnreadCounter<T>({
  dataName = 'directMessageReceipt/created',
  dataPath = '/support-chat',
  dataCb,
  dataNavCb,
  children,
  ...props
}: WebSocketUnreadCounterProps<T> &
  DetailedHTMLProps<InputHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>) {
  const [count, setCount] = useState<number>(0);

  const { urlPathname } = usePageContext();
  const authToken = useAuthToken();
  const mapRef = useRef<Map<number | string, number>>(new Map());
  const me = useMe();

  const idle = useIdleContext();
  const inactiveRef = useRef(idle.inactive);

  const setAppBadge = useCallback((count: number) => {
    if (!IS_SERVER && 'setAppBadge' in navigator) {
      if (count <= 0) {
        navigator.clearAppBadge();
      } else {
        navigator.setAppBadge(count);
      }
    }
  }, []);

  useEffect(() => {
    inactiveRef.current = idle.inactive;
  }, [idle.inactive]);

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  const onUnreadMessage = useCallback((event: MessageEvent, data: API.SubscriptionResponse<T>) => {
    // We don't use urlPathname so it prevents this callback from being re-bound on navigation
    const pathname = IS_SERVER ? urlPathname : location.pathname;

    if (dataCb) {
      if (pathname !== dataPath || inactiveRef.current) {
        dataCb(event, data, setCount, mapRef, me);
      }
    } else if (pathname !== dataPath || inactiveRef.current) {
      setCount((oldCount) => {
        mapRef.current.set(dataPath, oldCount + 1);

        return oldCount + 1;
      });
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  useEffect(() => {
    if (dataNavCb) {
      dataNavCb(urlPathname, setCount, mapRef, me);
    } else if (urlPathname === dataPath) {
      mapRef.current.delete(dataPath);
      setCount(0);
    }
  }, [urlPathname]);

  useUpdateEffect(() => {
    if (!idle.inactive) {
      const mapCount = mapRef.current.get(location.pathname);

      if (mapCount) {
        mapRef.current.delete(location.pathname);
        setCount((oldCount) => Math.max(oldCount - mapCount, 0));
      }
    }
  }, [idle.inactive]);

  useWebSocket(WS_BASE_URL, {
    connect: !!authToken,
    subscriptions: {
      [dataName]: onUnreadMessage,
    },
  });

  useEffect(() => setAppBadge(count), [setAppBadge, count]);

  return (
    <a {...props} data-count={count ? count : undefined}>
      {children}
    </a>
  );
}
